import { Badge } from "@/components/ui/badge";

import {
  stripSegments,
  type SyncScene,
  type SyncTimeline,
} from "./syncTimeline";

// ---------------------------------------------------------------------------
// SceneStrip — proportional-width clickable scene segments for the Director View
// sync viewer. Widths follow realized durations; title cards get a "T" glyph;
// skipped scenes collapse to a dot; the logo tail is hatched; audio padding is
// a muted filler. Manual timelines render unsized ordered chips instead.
// ---------------------------------------------------------------------------

/** 1 s montage cuts stay clickable: never narrower than this share. */
const MIN_SEGMENT_PERCENT = 1.2;

const HATCH_STYLE: React.CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(45deg, transparent, transparent 4px, currentColor 4px, currentColor 5px)",
};

function sceneGlyph(scene: SyncScene): string | null {
  if (scene.sceneType === "TitleCardScene") return "T";
  if (scene.sceneType === "VideoScene") return "▶";
  return null;
}

export function SceneStrip({
  timeline,
  activeIndex,
  onSeekToScene,
  orientationLocked,
  sceneRooms,
}: {
  timeline: SyncTimeline;
  activeIndex: number;
  onSeekToScene: (scene: SyncScene) => void;
  /** Scene index -> orientation anchor phrase ("frozen position" scenes). */
  orientationLocked?: Map<number, string> | null;
  /** Scene index -> room name from the director's room map (FPR-033). */
  sceneRooms?: Map<number, string> | null;
}) {
  const sceneByIndex = new Map(
    timeline.scenes.map((scene) => [scene.index, scene]),
  );

  if (timeline.source === "manual") {
    return (
      <div
        className="flex flex-wrap items-center gap-1.5"
        role="listbox"
        aria-label="Scenes (manual — no timing data)"
      >
        {timeline.scenes.map((scene) => (
          <button
            key={scene.index}
            type="button"
            role="option"
            aria-selected={scene.index === activeIndex}
            onClick={() => onSeekToScene(scene)}
            className="focus-visible:ring-ring rounded focus-visible:outline-none focus-visible:ring-2"
          >
            <Badge
              variant={scene.index === activeIndex ? "default" : "outline"}
              className="cursor-pointer"
            >
              {sceneGlyph(scene) ?? `S${scene.sceneId ?? scene.index + 1}`}
            </Badge>
          </button>
        ))}
      </div>
    );
  }

  const segments = stripSegments(timeline);
  if (segments.length === 0) return null;
  const start = timeline.posterOffsetSeconds;
  const end = segments[segments.length - 1].endSeconds;
  const total = Math.max(end - start, 0.001);
  const percentOf = (from: number, to: number) =>
    Math.max(((to - from) / total) * 100, 0);

  return (
    <div
      className="flex h-8 w-full items-stretch overflow-hidden rounded-md border bg-muted/40"
      role="listbox"
      aria-label="Scene timeline (click to seek)"
    >
      {segments.map((segment) => {
        if (segment.kind === "logo") {
          return (
            <div
              key="logo"
              title="Logo outro (no source media)"
              className="flex items-center justify-center text-[10px] uppercase tracking-wide text-muted-foreground/70"
              style={{
                width: `${percentOf(segment.startSeconds, segment.endSeconds)}%`,
                ...HATCH_STYLE,
              }}
            >
              logo
            </div>
          );
        }
        if (segment.kind === "padding") {
          return (
            <div
              key="padding"
              title="End padding (audio extended the file)"
              className="bg-muted/70"
              style={{
                width: `${percentOf(segment.startSeconds, segment.endSeconds)}%`,
              }}
            />
          );
        }
        const scene = sceneByIndex.get(segment.sceneIndex);
        if (!scene) return null;
        if (segment.skipped) {
          return (
            <button
              key={`skipped-${segment.sceneIndex}`}
              type="button"
              title={`Scene ${scene.sceneId ?? scene.index + 1} — skipped in the delivered cut`}
              aria-label={`Scene ${scene.sceneId ?? scene.index + 1} (skipped)`}
              onClick={() => onSeekToScene(scene)}
              className="flex w-2 flex-none items-center justify-center text-destructive"
            >
              •
            </button>
          );
        }
        const active = segment.sceneIndex === activeIndex;
        const width = Math.max(
          percentOf(segment.startSeconds, segment.endSeconds),
          MIN_SEGMENT_PERCENT,
        );
        const glyph = sceneGlyph(scene);
        const anchor = orientationLocked?.get(segment.sceneIndex) ?? null;
        const room = sceneRooms?.get(segment.sceneIndex) ?? null;
        const description = `Scene ${scene.sceneId ?? scene.index + 1} · ${
          scene.sceneType ?? "scene"
        } · ${segment.startSeconds.toFixed(1)}–${segment.endSeconds.toFixed(1)} s${
          anchor ? ` · frozen position ("${anchor}")` : ""
        }${room ? ` · ${room}` : ""}`;
        return (
          <button
            key={segment.sceneIndex}
            type="button"
            role="option"
            aria-selected={active}
            title={description}
            aria-label={description}
            onClick={() => onSeekToScene(scene)}
            className={`relative flex items-center justify-center border-r text-[10px] font-medium transition-colors last:border-r-0 ${
              active
                ? "bg-primary text-primary-foreground"
                : "bg-background text-muted-foreground hover:bg-accent"
            } ${scene.fallback ? "ring-1 ring-inset ring-amber-500/70" : ""}`}
            style={{ width: `${width}%` }}
          >
            {glyph ??
              (width > 3 ? String(scene.sceneId ?? scene.index + 1) : "")}
            {anchor ? (
              <span
                aria-hidden="true"
                data-testid={`frozen-position-marker-${segment.sceneIndex}`}
                className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-sky-500"
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
